// Normalize a string for case- and whitespace-insensitive comparison. Compare-only: callers still
// display the original value, so casing (acronyms) and punctuation are never altered.
export const normalizeForComparison = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ')

// US state qualifiers a flat location appends but a buyer usually omits ("Amherst County, VA" vs
// the buyer "County of Amherst"). Captured and normalized to a 2-letter code so the trailing state
// neither defeats the place comparison nor merges two genuinely different states ("Springfield, MA"
// vs "Springfield, IL"). Both the postal code and the spelled-out name occur in the feed.
const STATE_CODES = new Set([
  'al',
  'ak',
  'az',
  'ar',
  'ca',
  'co',
  'ct',
  'de',
  'fl',
  'ga',
  'hi',
  'id',
  'il',
  'in',
  'ia',
  'ks',
  'ky',
  'la',
  'me',
  'md',
  'ma',
  'mi',
  'mn',
  'ms',
  'mo',
  'mt',
  'ne',
  'nv',
  'nh',
  'nj',
  'nm',
  'ny',
  'nc',
  'nd',
  'oh',
  'ok',
  'or',
  'pa',
  'ri',
  'sc',
  'sd',
  'tn',
  'tx',
  'ut',
  'vt',
  'va',
  'wa',
  'wv',
  'wi',
  'wy',
  'dc',
  'pr',
  'gu',
  'vi',
  'as',
  'mp',
])

const STATE_NAME_TO_CODE: Record<string, string> = {
  alabama: 'al',
  alaska: 'ak',
  arizona: 'az',
  arkansas: 'ar',
  california: 'ca',
  colorado: 'co',
  connecticut: 'ct',
  delaware: 'de',
  florida: 'fl',
  georgia: 'ga',
  hawaii: 'hi',
  idaho: 'id',
  illinois: 'il',
  indiana: 'in',
  iowa: 'ia',
  kansas: 'ks',
  kentucky: 'ky',
  louisiana: 'la',
  maine: 'me',
  maryland: 'md',
  massachusetts: 'ma',
  michigan: 'mi',
  minnesota: 'mn',
  mississippi: 'ms',
  missouri: 'mo',
  montana: 'mt',
  nebraska: 'ne',
  nevada: 'nv',
  'new hampshire': 'nh',
  'new jersey': 'nj',
  'new mexico': 'nm',
  'new york': 'ny',
  'north carolina': 'nc',
  'north dakota': 'nd',
  ohio: 'oh',
  oklahoma: 'ok',
  oregon: 'or',
  pennsylvania: 'pa',
  'rhode island': 'ri',
  'south carolina': 'sc',
  'south dakota': 'sd',
  tennessee: 'tn',
  texas: 'tx',
  utah: 'ut',
  vermont: 'vt',
  virginia: 'va',
  washington: 'wa',
  'west virginia': 'wv',
  wisconsin: 'wi',
  wyoming: 'wy',
  'district of columbia': 'dc',
  'puerto rico': 'pr',
  guam: 'gu',
  'virgin islands': 'vi',
  'american samoa': 'as',
  'northern mariana islands': 'mp',
}

// The canonical 2-letter code for a trailing qualifier, or null if it is not a state.
const stateCode = (tail: string): string | null => {
  if (STATE_CODES.has(tail)) {
    return tail
  }
  return STATE_NAME_TO_CODE[tail] ?? null
}

// County-level designators collapse to 'county'; municipal ones stay distinct so a town and a
// village of the same name are not treated as one place.
const COUNTY_DESIGNATORS = ['county', 'parish']
const MUNICIPAL_DESIGNATORS = ['city', 'town', 'village', 'township', 'borough']
// State-level prefixes; the name after them is the state itself ("State of Alabama").
const STATE_DESIGNATOR_PREFIXES = ['state of ', 'commonwealth of ']

type PlaceDesignator =
  | 'county'
  | 'city'
  | 'town'
  | 'village'
  | 'township'
  | 'borough'
  | 'state'
  | 'none'

type ParsedPlace = {
  core: string
  designator: PlaceDesignator
  state: string | null
}

// Canonicalize a place label to its bare name, its civil-division type, and its state. The buyer
// and the flat location name the same place with the civil-division word in different positions -- a
// prefix ("County of Amherst", "Town of Ellicott"), a reordered comma-suffix ("Ellicott, Town of"),
// or the bare " County" suffix ("Amherst County") -- so all of those collapse to the same core. A
// bare " City"/" Town" SUFFIX is deliberately NOT stripped: it would turn "Kansas City" into
// "Kansas". The designator and state are kept (not discarded) so distinct places that share a name
// stay distinct -- see sameCivilPlace.
const parsePlace = (label: string): ParsedPlace => {
  let s = normalizeForComparison(label.replace(/`/g, ''))

  let state: string | null = null
  const comma = s.lastIndexOf(', ')
  if (comma !== -1) {
    const code = stateCode(s.slice(comma + 2))
    if (code) {
      state = code
      s = s.slice(0, comma)
    }
  }

  const designatorOf = (d: string): PlaceDesignator =>
    COUNTY_DESIGNATORS.includes(d) ? 'county' : (d as PlaceDesignator)

  // "State of X" / "Commonwealth of X" name the state itself. Keep the plain name as the core (so
  // it restates the flat state name "Alabama") and record the state it denotes, so a same-named
  // place in another state -- "Virginia, MN" against the Commonwealth of Virginia -- stays
  // distinct. The leading "the" of "Commonwealth of the Northern Mariana Islands" is dropped.
  for (const prefix of STATE_DESIGNATOR_PREFIXES) {
    if (s.startsWith(prefix)) {
      const rest = s.slice(prefix.length).replace(/^the /, '').trim()
      return {
        core: rest,
        designator: 'state',
        state: stateCode(rest) ?? state,
      }
    }
  }

  for (const d of [...COUNTY_DESIGNATORS, ...MUNICIPAL_DESIGNATORS]) {
    if (s.startsWith(`${d} of `)) {
      return {
        core: s.slice(d.length + 4).trim(),
        designator: designatorOf(d),
        state,
      }
    }
    if (s.endsWith(`, ${d} of`)) {
      return {
        core: s.slice(0, s.length - (d.length + 5)).trim(),
        designator: designatorOf(d),
        state,
      }
    }
  }
  // A bare trailing " county"/" parish" is an unambiguous county marker (unlike " city").
  for (const d of COUNTY_DESIGNATORS) {
    if (s.endsWith(` ${d}`)) {
      return {
        core: s.slice(0, s.length - (d.length + 1)).trim(),
        designator: 'county',
        state,
      }
    }
  }
  return { core: s, designator: 'none', state }
}

// A bare name (no designator) denotes a populated place or a state -- a county always carries the
// word "County"/"Parish" -- so it may restate a municipal or state label but never a county one.
// Two explicit designators must be equal (a town does not restate a village, nor a city a county).
const designatorsCompatible = (
  a: PlaceDesignator,
  b: PlaceDesignator,
): boolean => {
  if (a === b) {
    return true
  }
  if (a === 'none' || b === 'none') {
    return (a === 'none' ? b : a) !== 'county'
  }
  return false
}

// Two explicit states must match; a label without a state can restate one that has it (the location
// appends the state the buyer omits).
const statesCompatible = (a: string | null, b: string | null): boolean =>
  a === null || b === null || a === b

// The state a label names outright, as a canonical code: an explicit "State of X" designator, or a
// bare label that is itself a state name or postal code ("Virginia", "VA") with no trailing
// qualifier. A trailing state ("Virginia, MN") marks a place *in* that state, not the state, so it
// is excluded -- that keeps the borough of Indiana, PA a place rather than the state of Indiana.
const wholeStateCode = (p: ParsedPlace): string | null => {
  if (p.designator === 'state') {
    return p.state ?? stateCode(p.core)
  }
  if (p.designator === 'none' && p.state === null) {
    return stateCode(p.core)
  }
  return null
}

// True when two labels name the same civil place: same bare name, compatible civil-division type,
// and compatible state. This is what keeps "Lancaster County" (the county) distinct from
// "Lancaster, SC" (the city) and "Springfield, MA" distinct from "Springfield, IL".
const sameCivilPlace = (a: string, b: string): boolean => {
  const pa = parsePlace(a)
  const pb = parsePlace(b)
  if (!pa.core || !pb.core) {
    return false
  }
  // A state's full name and its postal code name one jurisdiction, so compare them as aliases when
  // both labels name a whole state ("State of Virginia" vs "VA").
  const sa = wholeStateCode(pa)
  const sb = wholeStateCode(pb)
  if (sa !== null && sb !== null) {
    return sa === sb
  }
  return (
    pa.core === pb.core &&
    designatorsCompatible(pa.designator, pb.designator) &&
    statesCompatible(pa.state, pb.state)
  )
}

// True when `outer` and `inner` name the same place, so a caller can drop the redundant one and show
// only the richer label. Two labels match when they normalize equal, or when they canonicalize to
// the same civil place (sameCivilPlace) -- which absorbs the common "buyer plus the state" case
// ("Plainfield, Town of" vs "Plainfield, Town of, CT"), reordered designators ("Town of Ellicott"
// vs "Ellicott, Town of"), and a bare name against its typed form ("Lake City, SC" vs "City of Lake
// City"). Backticks are stripped first: search highlights wrap matched terms in them, and that
// markup lands asymmetrically across the two labels, so leaving it in would defeat the comparison.
export const labelRestates = (outer: string, inner: string): boolean => {
  const a = normalizeForComparison(outer.replace(/`/g, ''))
  const b = normalizeForComparison(inner.replace(/`/g, ''))
  if (!a || !b) {
    return false
  }
  if (a === b) {
    return true
  }
  return sameCivilPlace(outer, inner)
}

export const capitalizeFirstLetter = (target: string) => {
  return target.charAt(0).toUpperCase() + target.slice(1)
}

export const arrayToCommaSeparatedString = (values: Array<string>) => {
  return values
    .map((value: any, index: number) => {
      if (index < values.length - 2) {
        return `${value}, `
      } else if (index === values.length - 2) {
        return `${value}${values.length === 2 ? '' : ','}  and `
      } else {
        return value
      }
    })
    .join('')
}

export const formatPhoneNumber = (phoneNumberString: string): string | null => {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3]
  }
  return null
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const noCentsCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export const formatDollarAmount = (amount: number) => {
  return currencyFormatter.format(amount)
}

export const formatDollarAmountWithSuffix = (
  amount: number,
  decimals: number = 0,
) => {
  if (amount >= 1e12) {
    return `$${(amount / 1e12).toFixed(decimals)}T`
  } else if (amount >= 1e9) {
    return `$${(amount / 1e9).toFixed(decimals)}B`
  } else if (amount >= 1e6) {
    return `$${(amount / 1e6).toFixed(decimals)}M`
  } else if (amount >= 1e3) {
    return `$${(amount / 1e3).toFixed(decimals)}K`
  } else {
    return `$${amount.toFixed(decimals)}`
  }
}

export const formatNumberWithSuffix = (
  amount: number,
  decimals: number = 2,
) => {
  if (amount >= 1e12) {
    const value = amount / 1e12
    return `${value.toFixed(decimals)}T`
  } else if (amount >= 1e9) {
    const value = amount / 1e9
    return `${value.toFixed(decimals)}B`
  } else if (amount >= 1e6) {
    const value = amount / 1e6
    return `${value.toFixed(decimals)}M`
  } else if (amount >= 1e3) {
    const value = amount / 1e3
    return `${value.toFixed(decimals)}K`
  } else {
    return `${amount}`
  }
}

export const formatDollarAmountNoCents = (amount: number) => {
  return noCentsCurrencyFormatter.format(amount)
}

export const isValidNumber = (number: string | null) => {
  if (number === null) {
    return false
  }
  return !isNaN(Number(number))
}

export const capitalizeString = (str: string) => {
  const ignoreWords = ['and', 'or', 'but', 'nor', 'for', 'yet', 'so', 'of']
  return str
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      // capitalize the first word or non-ignoreWords
      if (index === 0 || !ignoreWords.includes(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1)
      } else if (word.includes('-')) {
        return word
          .split('-')
          .map(
            (part) =>
              part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
          )
          .join('-')
      }
      return word
    })
    .join(' ')
}

export const removeMarkdownFormatting = (text: string) => {
  // regular expression to match Markdown links, images, bold, italic, etc.
  return text
    .replace(/[#*_~`]/g, '') // Remove headers, bold, italic, strikethrough, code
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Replace links with just the text
    .replace(/^\s*[-+*]\s/gm, '') // Remove list markers
    .replace(/^\s*\d+\.\s/gm, '') // Remove numbered list markers
    .trim()
}

export const removeAllMarkdownFormattingExceptCode = (text: string) => {
  return text
    .replace(/[#*_~]/g, '') // Remove headers, bold, italic, strikethrough
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Replace links with just the text
    .replace(/^\s*[-+*]\s/gm, '') // Remove list markers
    .replace(/^\s*\d+\.\s/gm, '') // Remove numbered list markers
    .trim()
}

export const removeCodeMarkdown = (text: string) => {
  return text.replace(/[`]/g, '')
}

export const formatNumberWithCommas = (number: number | string): string => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const isMarkdownTable = (text: string) => {
  return text.startsWith('|')
}

export const jaro = (s1: string, s2: string): number => {
  if (s1.length === 0 && s2.length === 0) return 1
  if (s1.length === 0 || s2.length === 0) return 0

  const matchDistance = Math.floor(Math.max(s1.length, s2.length) / 2) - 1

  const s1Matches: boolean[] = new Array(s1.length).fill(false)
  const s2Matches: boolean[] = new Array(s2.length).fill(false)

  let matches = 0
  for (let i = 0; i < s1.length; i++) {
    const start = Math.max(0, i - matchDistance)
    const end = Math.min(i + matchDistance + 1, s2.length)

    for (let j = start; j < end; j++) {
      if (s2Matches[j]) continue
      if (s1[i] !== s2[j]) continue
      s1Matches[i] = true
      s2Matches[j] = true
      matches++
      break
    }
  }

  if (matches === 0) return 0

  let transpositions = 0
  let k = 0
  for (let i = 0; i < s1.length; i++) {
    if (!s1Matches[i]) continue
    while (!s2Matches[k]) k++
    if (s1[i] !== s2[k]) transpositions++
    k++
  }

  return (
    (matches / s1.length +
      matches / s2.length +
      (matches - transpositions / 2) / matches) /
    3
  )
}

export const jaroWinkler = (s1: string, s2: string): number => {
  const match = (str1: string, str2: string) => {
    if (str1.length === 0 || str2.length === 0) return 0
    let matches = 0
    const range = Math.floor(Math.max(str1.length, str2.length) / 2) - 1
    const matchIndexes: boolean[] = Array(str1.length).fill(false)
    const matchFlags: boolean[] = Array(str2.length).fill(false)

    for (let i = 0; i < str1.length; i++) {
      const low = Math.max(0, i - range)
      const high = Math.min(i + range + 1, str2.length)
      for (let j = low; j < high; j++) {
        if (!matchFlags[j] && str1[i] === str2[j]) {
          matchIndexes[i] = true
          matchFlags[j] = true
          matches++
          break
        }
      }
    }
    return matches
  }

  const matches = match(s1, s2)
  if (matches === 0) return 0

  let t = 0
  let point = 0
  for (let i = 0; i < s1.length; i++) {
    if (s1[i] === s2[point]) point++
    else {
      point = 0
      t++
    }
  }
  t /= 2

  const l = 4
  const p = 0.1
  const jaro =
    (matches / s1.length + matches / s2.length + (matches - t) / matches) / 3
  return jaro + l * p * (1 - jaro)
}

export const fuzzyMatch = (
  text: string,
  pattern: string,
  threshold: number = 0.85,
  algorithm: 'jaro' | 'jaroWinkler' = 'jaro',
): boolean => {
  if (text.toLowerCase().includes(pattern.toLowerCase())) return true
  return algorithm === 'jaro'
    ? jaro(text.toLowerCase(), pattern.toLowerCase()) >= threshold
    : jaroWinkler(text.toLowerCase(), pattern.toLowerCase()) >= threshold
}

export const isAlphaNumeric = (char: string | undefined) => {
  if (!char) return false
  return /^[a-zA-Z0-9]+$/.test(char)
}

/**
 * Sanitize a dollar input string: allow digits and at most one decimal with up to 2 places.
 */
export const sanitizeDollarInput = (value: string): string => {
  const raw = value.replace(/[^0-9.]/g, '')
  const parts = raw.split('.')
  if (parts.length > 2) {
    const joined = parts.slice(1).join('')
    return `${parts[0]}.${joined.slice(0, 2)}`
  }
  if (parts.length === 2 && parts[1].length > 2)
    return `${parts[0]}.${parts[1].slice(0, 2)}`
  return raw
}

export const replaceNewLinesWithBr = (markdown: string) => {
  return markdown.replace(/([^\|])\n/g, '$1<br />')
}
